import { ITimeoutService } from "angular";
import { Ng1StateDeclaration, StateService, StateParams } from "@uirouter/angularjs";
import { IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { modal, fixModalsAbstractStatesIE } from "@gtmhub/core/routing";
import { storage } from "@gtmhub/core/storage";

let widgetsModal: IModalServiceInstance;
const widgetStates = (parent: string): Ng1StateDeclaration[] => [
  {
    name: `${parent}.widgets`,
    abstract: true,
    onEnter: [
      "$uibModal",
      "$state",
      ($uibModal: IModalService, $state: StateService) => {
        widgetsModal = $uibModal.open({
          template: "<div ui-view='widgetsmodalview'></div>",
          backdrop: true,
          windowClass: "sidebar-modal widget-browser state-initiated",
        });

        // clicking on the backdrop will trigger dismiss(), so we go up
        widgetsModal.result.catch(() => $state.go("^.^", { widget: null, widgets: null }));
      },
    ],
    onExit: () => {
      widgetsModal.close();
    },
  },
  {
    name: `${parent}.widgets.browser`,
    url: "widgets/?widgetsDashboardId",
    data: {
      trackingPage: "Insightboard Widget Details",
    },
    params: {
      widgetsDashboardId: null,
    },
    views: {
      "widgetsmodalview@": {
        templateUrl: require("../insightboards/views/insightboard-widget-browser.jade"),
        controller: "InsightboardWidgetBrowserCtrl",
      },
    },
  },
  {
    name: `${parent}.widgets.done`,
    params: {
      widget: null,
      widgets: null,
    },
    onEnter: [
      "$state",
      "$stateParams",
      ($state: StateService, $stateParams: StateParams) => {
        const widget = $stateParams.widget;
        const widgets = $stateParams.widgets;

        return $state.target("gtmhub.insightboard", { widget, widgets });
      },
    ],
  },
  {
    name: `${parent}.widgets.close`,
    onEnter: ["$state", ($state: StateService) => $state.target($state.get("^.^"), { widget: null, widgets: null })],
  },
];

const connectorConfigure = (name: string, returnState: string): Ng1StateDeclaration => ({
  name,
  params: {
    returnState: returnState || "^.connectorsList",
    dataSourceBlueprintName: null,
    connectionId: null,
    requiredEntities: null,
  },
  url: ":dataSourceBlueprintName/configure/?connectionId",
  views: {
    "modalview@": {
      templateUrl: require("../datasources/views/data-source-connect.jade"),
      controller: "ConfigureConnectorCtrl",
    },
  },
});

let dataSourceAddAbstractStateModal: IModalServiceInstance;

const dataSourceAddAbstractState = (name: string, args: { parent: string; newModal?: boolean }): Ng1StateDeclaration => ({
  name,
  abstract: true,
  url: "add-data-source/",
  data: {
    datasource: null,
    trackingPage: "Add Data Source",
  },
  onEnter: [
    "$uibModal",
    "$state",
    ($uibModal: IModalService, $state: StateService) => {
      fixModalsAbstractStatesIE();

      if (args.newModal) {
        dataSourceAddAbstractStateModal = $uibModal.open({
          template: "<div ui-view='modalview'></div>",
          backdrop: true,
          windowClass: "datasource-editor state-initiated",
        });

        dataSourceAddAbstractStateModal.result.then(
          function () {
            $state.go(args.parent);
          },
          function () {
            storage.set("selectedPlan", null);
            // clearing selectedPlans from store when clicking outside modal
            // cancelConnect param is needed if canceling "Install from gallery"
            $state.go(args.parent, {
              cancelConnect: true,
            });
          }
        );

        dataSourceAddAbstractStateModal.result.finally(() => {
          $state.go(args.parent);
        });
      }
    },
  ],
  onExit: () => {
    dataSourceAddAbstractStateModal.close();
  },
});

const connectorsList = (name: string): Ng1StateDeclaration => ({
  name,
  url: "connectors/?activeCategoryName&highlightedBlueprintName",
  params: {
    returnUrl: "gtmhub.dataflow",
    entityTitle: null,
    highlightedBlueprintName: { dynamic: true },
    activeCategoryName: { dynamic: true },
  },
  data: {
    trackingPage: "Connectors List",
  },
  views: {
    "modalview@": {
      templateUrl: require("../datasources/views/data-source-browser.jade"),
      controller: "ConnectorsCtrl",
    },
  },
});

const connectorSelector = (name: string): Ng1StateDeclaration => ({
  name,
  url: ":dataSourceBlueprintName/connectors-select/",
  params: {
    returnUrl: "",
    dataSourceBlueprintName: null,
    dataSourceBlueprintLogoUrl: null,
    returnState: "",
    requiredEntities: null,
  },
  data: {
    trackingPage: "Connector Selector",
  },
  views: {
    "modalview@": {
      templateUrl: require("../datasources/views/datasource-select-connector.jade"),
      controller: "ConnectorSelectorCtrl",
    },
  },
});

const selectRemoteEntities = (name: string): Ng1StateDeclaration => ({
  name,
  params: {
    connectorId: null,
    entityTitle: null,
    dataSourceBlueprintName: null,
  },
  url: "{connectorId}/",
  views: {
    "modalview@": {
      templateUrl: require("../datasources/views/file-browser.jade"),
      controller: "RemoteEntitiesBrowserCtrl",
    },
  },
});

const dataSourceFinish = (name: string, parent?: string): Ng1StateDeclaration => ({
  name,
  onEnter: [
    "$state",
    ($state: StateService) => {
      dataSourceAddAbstractStateModal.close();

      if (parent) {
        return $state.target(parent);
      }
    },
  ],
});

const setSyncSchedule = (name: string): Ng1StateDeclaration => ({
  name,
  params: {
    connectorId: null,
    remoteEntity: null,
    remoteEntityNamespace: null,
    entityTitle: null,
    entityTypeName: null,
  },
  url: ":connectorId/:remoteEntity/set-name-and-schedule/",
  views: {
    "modalview@": {
      templateUrl: require("../datasources/views/data-source-confirm.jade"),
      controller: "SyncScheduleCtrl",
    },
  },
});

const manageEntityAbstractState = (name: string) =>
  modal({
    name,
    view: "modalview",
    abstract: true,
    url: "manage/entity/",
    params: {
      entity: null,
    },
  });

const manageEntityState = (name: string): Ng1StateDeclaration => ({
  name,
  url: ":entityName/",
  params: {
    entityName: null,
  },
  views: {
    "modalview@": {
      templateUrl: require("../entities/views/manage-entity.jade"),
      controller: "EntityCtrl",
    },
  },
});

const createInsightboardFormAbstractState = (name: string) =>
  modal({
    name,
    view: "formmodal",
    windowClass: "medium-small",
    abstract: true,
  });

const createInsightboardEditState = (name: string): Ng1StateDeclaration => ({
  name,
  url: "edit/:editDashboardId/",
  params: {
    editDashboardId: null,
    toBeCloned: false,
    redirectToClonedOnSave: false,
  },
  data: {
    modal: null,
    requiresLogin: true,
    trackingPage: "Insightboard Edit",
  },
  views: {
    "formmodal@": {
      templateUrl: require("../insightboards/views/insightboards-details.jade"),
      controller: "EditInsightboardCtrl",
    },
  },
});

const createInsightboardTagsState = (name: string): Ng1StateDeclaration => ({
  name,
  url: "edit-tags/:tagsDashboardId/",
  params: {
    tagsDashboardId: null,
  },
  data: {
    modal: null,
    requiresLogin: true,
    trackingPage: "Insight Edit Tags",
  },
  views: {
    "formmodal@": {
      templateUrl: require("../insightboards/views/insightboards-tags.jade"),
      controller: "EditInsightboardTagsCtrl",
    },
  },
});

const revisionsAbstractState = (name: string) =>
  modal({
    name,
    view: "insightrevisions",
    windowClass: "large insight-revisions-modal",
    abstract: true,
    backdrop: true,
  });

const revisionsState = (name: string): Ng1StateDeclaration => ({
  name,
  url: "revisions/:insightName",
  data: {
    trackingPage: "Insight Revisions",
  },
  views: {
    "insightrevisions@": {
      templateUrl: require("./views/insight-revisions.jade"),
      controller: "InsightRevisionsCtrl",
    },
  },
});

const usageAbstractState = (name: string) =>
  modal({
    name,
    view: "modalview",
    windowClass: "medium",
    abstract: true,
    url: "usage/",
  });

const usageState = (name: string): Ng1StateDeclaration => ({
  name,
  data: {
    requiresLogin: true,
    trackingPage: "Insight Usage",
  },
  params: {
    insightUniqueName: null,
  },
  url: ":insightUniqueName/",
  views: {
    "modalview@": {
      templateUrl: require("./views/usage.jade"),
      controller: "InsightUsageCtrl",
    },
  },
});

export const templatesAbstractState = (name: string): Ng1StateDeclaration =>
  modal({
    name,
    view: "modalview",
    windowClass: "medium",
    abstract: true,
  });

export const mySnippetsState = (name: string): Ng1StateDeclaration => ({
  name,
  url: "my-snippets/",
  views: {
    "modalview@": {
      templateUrl: require("./views/my-snippets.jade"),
      controller: "MySnippetsCtrl",
    },
  },
});

/**
 * Insights
 */
const insightStates: Ng1StateDeclaration[] = [
  {
    name: "gtmhub.insight",
    data: {
      requiresLogin: true,
      titleExpression: "insight.title",
      styleUrl: require("./insights.module.less"),
      trackingPage: "Insight",
    },
    params: {
      name: null,
      dashboardId: null,
      newInsight: null,
      kpiId: null,
    },
    url: "/insight/:name/?dashboardId&newInsight",
    templateUrl: require("./views/insight-details.jade"),
    controller: "InsightCtrl",
  },
  revisionsAbstractState("gtmhub.insight._revisions"),
  revisionsState("gtmhub.insight._revisions.revisions"),
  templatesAbstractState("gtmhub.insight._templates"),
  {
    name: "gtmhub.insight._templates.chooseTemplate",
    url: "templates/",
    data: {
      trackingPage: "Insight Templates",
    },
    views: {
      "modalview@": {
        templateUrl: require("./views/templates.jade"),
        controller: "InsightTemplatesCtrl",
      },
    },
  },
  {
    name: "gtmhub.insight._templates.chooseSnippet",
    url: "snippets/",
    data: {
      trackingPage: "Insight Snippets",
    },
    views: {
      "modalview@": {
        templateUrl: require("./views/snippets.jade"),
        controller: "InsightSnippetsCtrl",
      },
    },
  },
  mySnippetsState("gtmhub.insight._templates.mySnippets"),
  {
    name: "gtmhub.insightCodelessEditor",
    data: {
      requiresLogin: true,
      trackingPage: "Insight Codeless Editor",
    },
    params: {
      name: null,
      dashboardId: null,
      newInsight: null,
    },
    url: "/insight-codeless-editor/:name/?dashboardId",
    templateUrl: require("./views/insight-codeless-editor.jade"),
    controller: "InsightCodelessEditorCtrl",
  },

  // manage entity
  manageEntityAbstractState("gtmhub.insight.manage"),
  manageEntityState("gtmhub.insight.manage.entity"),
];

/**
 * Insightboards
 */
export const insightboardsStateName = "gtmhub.insightboards";

const insightboardsStates: Ng1StateDeclaration[] = [
  {
    name: insightboardsStateName,
    url: "/insightboards/",
    data: {
      modal: null,
      requiresLogin: true,
      titleKey: "insightboards",
      trackingPage: "Insightboards",
      styleUrl: require("./insights.module.less"),
    },
    templateUrl: require("../insightboards/views/insightboards-main.jade"),
    controller: "InsightboardsCtrl",
    reloadOnSearch: false,
  },
  usageAbstractState("gtmhub.insightboards.widgets.browser._usage"),
  usageState("gtmhub.insightboards.widgets.browser._usage.usageList"),
  revisionsAbstractState("gtmhub.insightboards.widgets.browser._revisions"),
  revisionsState("gtmhub.insightboards.widgets.browser._revisions.revisions"),
  createInsightboardFormAbstractState("gtmhub.insightboards._form"),
  {
    name: "gtmhub.insightboards._form.create",
    url: "create/",
    data: {
      modal: null,
      requiresLogin: true,
      trackingPage: "Insightboard Create",
    },
    views: {
      "formmodal@": {
        templateUrl: require("../insightboards/views/insightboards-details.jade"),
        controller: "CreateInsightboardCtrl",
      },
    },
  },
  createInsightboardFormAbstractState("gtmhub.insightboards._edit"),
  createInsightboardEditState("gtmhub.insightboards._edit.edit"),
  createInsightboardTagsState("gtmhub.insightboards._edit.tags"),
  ...widgetStates("gtmhub.insightboards"),
];

/**
 * Insightboard
 */
export const insightboardStateName = "gtmhub.insightboard";

const insightboardStates: Ng1StateDeclaration[] = [
  {
    name: insightboardStateName,
    url: "/insightboard/:dashboardId/?sessionId",
    params: {
      dashboardId: null,
      sessionId: null,
      widgets: { dynamic: true },
      widget: { dynamic: true },
    },
    data: {
      // manually track page in the controller because of reports name
      disableAnalytics: true,
      modal: null,
      requiresLogin: true,
      titleExpression: "insightboard.name",
      styleUrl: require("./insights.module.less"),
      trackingPage: "Insightboards",
    },
    templateUrl: require("../insightboards/views/insightboard.jade"),
    controller: "InsightboardCtrl",
  },
  usageAbstractState("gtmhub.insightboard.widgets.browser._usage"),
  usageState("gtmhub.insightboard.widgets.browser._usage.usageList"),
  revisionsAbstractState("gtmhub.insightboard.widgets.browser._revisions"),
  revisionsState("gtmhub.insightboard.widgets.browser._revisions.revisions"),
  createInsightboardFormAbstractState("gtmhub.insightboard._edit"),
  createInsightboardEditState("gtmhub.insightboard._edit.edit"),
  createInsightboardTagsState("gtmhub.insightboard._edit.tags"),
  modal({
    name: "gtmhub.insightboard._comments",
    view: "modalview",
    windowClass: "sidebar-modal",
    url: "comments/",
    abstract: true,
  }),
  {
    name: "gtmhub.insightboard._comments.timeline",
    data: {
      requiresLogin: true,
      trackingPage: "Insight Timeline",
    },
    params: {
      insightUniqueName: null,
    },
    url: ":insightUniqueName/timeline",
    views: {
      "modalview@": {
        templateUrl: require("./views/comments.jade"),
        controller: "InsightTimelineCtrl",
      },
    },
  },

  // add new connector / choose connector
  dataSourceAddAbstractState("gtmhub.insightboard.add", { parent: "gtmhub.insightboard", newModal: true }),
  connectorsList("gtmhub.insightboard.add.connectorsList"),
  connectorSelector("gtmhub.insightboard.add.connectorSelector"),
  connectorConfigure("gtmhub.insightboard.add.connectorConfigure", "gtmhub.insightboard"),

  ...widgetStates("gtmhub.insightboard"),
];

/**
 * Data Sources
 */
const dataSourcesStates: Ng1StateDeclaration[] = [
  // virtual data sources
  {
    name: "gtmhub.virtualDatasource",
    data: {
      requiresLogin: true,
      titleExpression: "ds.name",
      trackingPage: "Virtual Data Source",
    },
    url: "/virtual-data-source/:vdId/",
    templateUrl: require("../datasources/views/virtual-datasource.jade"),
    controller: "VirtualDatasourceCtrl",
  },
  templatesAbstractState("gtmhub.virtualDatasource._templates"),
  mySnippetsState("gtmhub.virtualDatasource._templates.mySnippets"),

  // data sources
  {
    name: "gtmhub.dataflow",
    data: {
      requiresLogin: true,
      titleKey: "data_sources",
      styleUrl: require("../datasources/data-sources.less"),
      trackingPage: "Data Sources",
    },
    url: "/data-sources/",
    templateUrl: require("../datasources/views/dataflow.jade"),
    controller: "DataflowCtrl",
  },

  // edit connection
  modal({
    name: "gtmhub.dataflow._editRight",
    view: "modalview",
    abstract: true,
  }),
  connectorConfigure("gtmhub.dataflow._editRight.connection", "^.^"),

  // delete connection
  modal({
    name: "gtmhub.dataflow._confirmMedium",
    view: "confirmmodalview",
    windowClass: "medium",
    abstract: true,
  }),
  {
    name: "gtmhub.dataflow._confirmMedium.deleteConnection",
    url: "delete-connection/:connectionId/",
    params: {
      connectionId: null,
    },
    views: {
      "confirmmodalview@": {
        templateUrl: require("../datasources/views/confirm-delete-connection.jade"),
        controller: "DeleteConnectionCtrl",
      },
    },
  },

  // add new data source
  dataSourceAddAbstractState("gtmhub.dataflow.add", { parent: "gtmhub.dataflow", newModal: true }),
  connectorsList("gtmhub.dataflow.add.connectorsList"),
  connectorSelector("gtmhub.dataflow.add.connectorSelector"),
  connectorConfigure("gtmhub.dataflow.add.connectorConfigure", "^.connectorsList"),
  selectRemoteEntities("gtmhub.dataflow.add.connected"),
  dataSourceFinish("gtmhub.dataflow.add.connected.done", "gtmhub.dataflow"),
  setSyncSchedule("gtmhub.dataflow.add.setSyncSchedule"),
  dataSourceFinish("gtmhub.dataflow.add.finish", "gtmhub.dataflow"),

  // delete data source
  modal({
    name: "gtmhub.dataflow._delete",
    view: "modalview",
    windowClass: "medium",
    abstract: true,
  }),
  {
    name: "gtmhub.dataflow._delete.confirm",
    url: "delete/:dataSourceId/",
    data: {
      trackingPage: "Data Source Delete",
    },
    views: {
      "modalview@": {
        templateUrl: require("../datasources/views/delete.jade"),
        controller: "DeleteDataSourceCtrl",
      },
    },
  },
  {
    name: "gtmhub.dataflow._delete.done",
    onEnter: [
      "$state",
      ($state: StateService) => {
        dataSourceAddAbstractStateModal.close();
        $state.go("^.^");
      },
    ],
  },

  // edit data source
  modal({
    name: "gtmhub.dataflow._edit",
    view: "editmodal",
    windowClass: "medium datasource-editor",
    abstract: true,
  }),
  {
    name: "gtmhub.dataflow._edit.edit",
    url: ":dataSourceId/edit/",
    params: {
      dataSourceId: null,
    },
    data: {
      requiresLogin: true,
      trackingPage: "Data Source edit",
    },
    views: {
      "editmodal@": {
        templateUrl: require("../datasources/views/edit-data-source.jade"),
        controller: "EditDataSourceCtrl",
      },
    },
  },

  // update data source schema
  modal({
    name: "gtmhub.dataflow._refreshSchema",
    view: "modalview",
    windowClass: "large",
    abstract: true,
  }),
  {
    name: "gtmhub.dataflow._refreshSchema.progress",
    url: "schema-refresh/:dataSourceId/",
    data: {
      trackingPage: "Data Source Refresh",
    },
    views: {
      "modalview@": {
        templateUrl: require("../datasources/views/schema-refresh.jade"),
        controller: "SchemaRefreshCtrl",
      },
    },
  },
  {
    name: "gtmhub.dataflow._refreshSchema.changes",
    url: "schema-changes/:dataSourceId/",
    views: {
      "modalview@": {
        templateUrl: require("../datasources/views/schema-changes.jade"),
        controller: "SchemaChangesCtrl",
      },
    },
  },
  {
    name: "gtmhub.dataflow._refreshSchema.done",
    onEnter: [
      "$state",
      ($state: StateService) => {
        dataSourceAddAbstractStateModal.close();
        $state.go("^.^");
      },
    ],
  },

  // connecting entity - mapping entity's fields to a datasource's fields
  modal({
    name: "gtmhub.dataflow.connect",
    view: "modalview",
    abstract: true,
    url: "{entityName}/",
  }),
  {
    name: "gtmhub.dataflow.connect.mapper",
    params: {
      entityName: null,
    },
    url: "mappings/",
    data: {
      trackingPage: "Data Source Entity Mappings",
    },
    views: {
      "modalview@": {
        templateUrl: require("../entities/views/entity-mapper.jade"),
        controller: "EntityMapperCtrl",
      },
    },
  },
  {
    name: "gtmhub.dataflow.connect.done",
    onEnter: [
      "$state",
      ($state: StateService) => {
        $state.go("gtmhub.dataflow");
      },
    ],
  },

  // wizard to add new datasource from the entities mapping screen
  connectorsList("gtmhub.dataflow.connect.connectorsList"),
  connectorSelector("gtmhub.dataflow.connect.connectorSelector"),
  connectorConfigure("gtmhub.dataflow.connect.connectorConfigure", "^.connectorsList"),
  selectRemoteEntities("gtmhub.dataflow.connect.connected"),
  setSyncSchedule("gtmhub.dataflow.connect.setSyncSchedule"),
  {
    name: "gtmhub.dataflow.connect.finish",
    onEnter: [
      "$state",
      "$stateParams",
      "$timeout",
      ($state: StateService, $stateParams: StateParams, $timeout: ITimeoutService) => {
        $timeout(() => {
          $state.go("gtmhub.dataflow.connect.mapper", {
            entityName: $stateParams.entityName,
          });
        });
      },
    ],
  },

  // create new entity
  modal({
    name: "gtmhub.dataflow._createEntity",
    view: "createmodalview",
    windowClass: "medium",
    abstract: true,
  }),
  {
    name: "gtmhub.dataflow._createEntity.createDialog",
    url: "create-entity/",
    data: {
      trackingPage: "Create Entity",
    },
    views: {
      "createmodalview@": {
        templateUrl: require("../entities/views/entities-create.jade"),
        controller: "CreateEntityCtrl",
      },
    },
  },

  // single entity
  manageEntityAbstractState("gtmhub.dataflow.manage"),
  manageEntityState("gtmhub.dataflow.manage.entity"),
  {
    name: "gtmhub.dataflow.manage.done",
  },

  // virtual data source
  modal({
    name: "gtmhub.dataflow._vdForm",
    view: "vdformmodalview",
    windowClass: "medium-small",
    abstract: true,
    url: "/virtual-data-source-form/",
  }),
  {
    name: "gtmhub.dataflow._vdForm.vdForm",
    url: ":virtualDatasourceId/",
    params: { virtualDatasourceId: null },
    views: {
      "vdformmodalview@": {
        templateUrl: require("../datasources/views/vd-form.jade"),
        controller: "VDFormCtrl",
      },
    },
  },

  // installer states
  {
    name: "gtmhub.welcome",
    url: "/welcome/",
    params: {
      cancelConnect: false,
    },
    data: {
      requiresLogin: true,
      trackingPage: "Insights Welcome",
    },
    templateUrl: require("../installers/views/welcome.jade"),
    controller: "WelcomeCtrl",
  },
  dataSourceAddAbstractState("gtmhub.welcome.installer", { parent: "gtmhub.welcome", newModal: true }),
  connectorConfigure("gtmhub.welcome.installer.configureDataSource", "^.cancel"),
  dataSourceFinish("gtmhub.welcome.installer.cancel"),
  dataSourceFinish("gtmhub.welcome.installer.connected"),
];

export default [...insightStates, ...insightboardStates, ...insightboardsStates, ...dataSourcesStates];
